import React, { useState, useEffect, useCallback, useRef } from 'react';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import SingleReply from './SingleReply';
import ItemReply from './ItemReply';
import ConversationApi from './api/ConversationApi';

import './ConversationCardReplies.css';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface Reply {
  ReplyId: number;
  ConversationPhaseId?: number;
  OriginalConversationId?: number;
  IsEnable: boolean;
  Body: string;
  ImageName: string;
  reloadReply?: boolean;
  newReply?: boolean;
}

interface Phase {
  ConversationPhaseId: number;
}

interface Conversation {
  ConversationId: number;
  Replies: Reply[];
  TotalReplies: number;
  TotalTasks: number;
  ProjectId: number;
  IsEnable: boolean;
  HasPhases?: boolean;
}

interface Config {
  weburl: string;
}

interface ConfigMenus {
  [key: string]: unknown;
}

interface CorgeeFunctions {
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  onUpdateConversation: (conversationId: number, conversation: Conversation, flag: boolean) => void;
}

interface StorageData {
  [key: string]: unknown;
}

interface ChatData {
  [key: string]: unknown;
}

interface ApiClient {
  [key: string]: unknown;
}

interface ConversationCardRepliesProps {
  conversation: Conversation;
  api: ApiClient;
  onUpdateTotalFavorites?: () => void;
  onDeleteReply?: (replyId: number) => void;
  corgeeFunctions: CorgeeFunctions;
  config: Config;
  configMenus?: ConfigMenus;
  isConversationDetailsView?: boolean;
  isInfoWindow?: boolean;
  isInfowindowViewCard?: boolean;
  SocketConnectionId?: string;
  seeMoreReplies?: boolean;
  pageNumber?: number;
  pageSize?: number;
  phaseSelected?: Phase;
  selectedReplyTab?: string;
  submitTime?: boolean;
  onAddRepost?: () => void;
  setShowRepost?: (show: boolean) => void;
  onUpdateReply?: (reply: Reply) => void;
  onResetRepliesList?: (replies: Reply[], flag?: boolean) => void;
  // True once the visible window covers the whole thread the API returned —
  // the gray bar uses it to decide See more vs See less, because comparing
  // shown vs TotalReplies breaks when the counter includes replies the fetch
  // does not return (layer-hidden, disabled, tasks counted separately).
  onRepliesWindowState?: (fullyShown: boolean) => void;
  setIsFromAddReply?: (value: boolean) => void;
  resetSeeMoreReplies?: () => void;
  repliesPendingToHighlight?: number[];
  removePendingHighlight?: (replyId: number) => void;
  storageData?: StorageData;
  chatData?: ChatData;
  isHomeView?: boolean;
  onLoadingMoreRepliesChange?: (loading: boolean) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

const isConvDetailsViewFromUrl = (): boolean =>
{
  return window.location.href.indexOf('ConversationDetails') > -1;
};

const _showActionMenu = (replyItem: Reply): boolean =>
{
  if (!replyItem.IsEnable)
  {
    return false;
  }

  const bodyHtml = `<div>${replyItem.Body}</div>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(bodyHtml, 'text/html');
  const hasDetailUpdateEmbed = doc.querySelector('.detail-update-embed');

  if (hasDetailUpdateEmbed)
  {
    return false;
  }

  return CorgeeStorageHelper.canDelete() || CorgeeStorageHelper.canEdit();
};
void _showActionMenu;

const normalizeImageNames = (replies: Reply[], weburl: string): Reply[] =>
{
  return replies.map(reply => ({
    ...reply,
    ImageName: reply.ImageName === 'Images/user.png' ? `${weburl}Images/user.png` : reply.ImageName,
  }));
};

// ============================================================================
// Component
// ============================================================================

const ConversationCardReplies: React.FC<ConversationCardRepliesProps> = ({
  conversation,
  api,
  onUpdateTotalFavorites,
  onDeleteReply,
  corgeeFunctions,
  config,
  configMenus,
  isConversationDetailsView = false,
  isInfoWindow = false,
  isInfowindowViewCard = false,
  SocketConnectionId,
  seeMoreReplies = false,
  pageNumber = 0,
  pageSize = 10,
  phaseSelected,
  selectedReplyTab = 'all',
  submitTime = false,
  onAddRepost,
  setShowRepost,
  onUpdateReply,
  onResetRepliesList,
  onRepliesWindowState,
  setIsFromAddReply,
  resetSeeMoreReplies,
  repliesPendingToHighlight,
  removePendingHighlight,
  storageData,
  chatData,
  isHomeView = false,
  onLoadingMoreRepliesChange,
  ...restProps
}) =>
{
  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  // Replies pagination, same in every view (dashboard cards, bookmarks, Home,
  // InfoWindow, Conversation Details): land collapsed on the last
  // COLLAPSED_REPLIES, each "See more replies" click reveals REPLIES_PER_PAGE
  // more, and the gray bar offers "See less" only once the whole thread is
  // visible.
  const COLLAPSED_REPLIES = 3;
  const REPLIES_PER_PAGE = 10;

  // Size of the visible window. Infinity while a deep-linked reply is pending
  // highlight, so the target reply is guaranteed to be present.
  const visibleCountRef = useRef<number>(
    (repliesPendingToHighlight?.length ?? 0) > 0 ? Number.POSITIVE_INFINITY : COLLAPSED_REPLIES,
  );

  // Phase tab currently on screen. 0 ("All Phases") or a conversation without
  // phases means no scoping.
  const activePhaseId = conversation.HasPhases ? (phaseSelected?.ConversationPhaseId ?? 0) : 0;

  // Every list that reaches the render state passes through this window, so no
  // data path (initial load, fetch, external update) can bypass the pagination.
  //
  // The phase filter lives here, ahead of the slice, for two reasons: not every
  // path that refills the list is phase-aware (the parent's conversation.Replies
  // after a reload carries every phase), and filtering after the slice would
  // leave fewer than the window's worth of rows on screen.
  const scopeToPhase = useCallback(
    (list: Reply[]): Reply[] =>
      activePhaseId
        ? list.filter(reply => (reply.ConversationPhaseId ?? 0) === activePhaseId)
        : list,
    [activePhaseId],
  );

  const applyWindow = useCallback(
    (list: Reply[]): Reply[] => scopeToPhase(list).slice(-visibleCountRef.current),
    [scopeToPhase],
  );

  // Length of the scoped thread as of the last render, plus the phase it belonged
  // to, so a genuine new reply can be told apart from a tab switch.
  const prevScopedLenRef = useRef<number>(0);
  const prevActivePhaseIdRef = useRef<number>(activePhaseId);

  const [conversationId, setConversationId] = useState<number>(conversation.ConversationId);
  const [replies, setReplies] = useState<Reply[]>(() => applyWindow(conversation.Replies || []));
  const [isConvDetailsView] = useState<boolean>(isConvDetailsViewFromUrl());

  // Refs para trackear valores previos
  const prevSeeMoreRepliesRef = useRef<boolean>(seeMoreReplies);
  const prevPageNumberRef = useRef<number>(pageNumber);
  const prevSubmitTimeRef = useRef<boolean>(submitTime);
  const prevRepliesRef = useRef<Reply[]>(replies);

  // Publish the initial window upstream: the gray bar's See more/less label
  // compares conversation.Replies (what is SHOWN) against TotalReplies, so it
  // must see the window, not the full thread the view may have loaded.
  useEffect(() =>
  {
    if ((conversation.Replies?.length ?? 0) > replies.length)
    {
      onResetRepliesList?.(replies, true);
      onRepliesWindowState?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase switch: restart the window so the new phase lands collapsed and the
  // next See more grows from the base again
  const prevPhaseIdRef = useRef<number>(phaseSelected?.ConversationPhaseId ?? 0);
  useEffect(() =>
  {
    const phaseId = phaseSelected?.ConversationPhaseId ?? 0;
    if (prevPhaseIdRef.current !== phaseId)
    {
      prevPhaseIdRef.current = phaseId;
      visibleCountRef.current = COLLAPSED_REPLIES;
    }
  }, [phaseSelected, COLLAPSED_REPLIES]);

  // --------------------------------------------------------------------------
  // Fetch Replies Logic
  // --------------------------------------------------------------------------
  const fetchReplies = useCallback(
    async (isSubmitTime: boolean = false) =>
    {
      const layersToHide = CorgeeStorageHelper.getLayersToHide();

      let conversationPhaseId = 0;
      if (conversation.HasPhases && phaseSelected)
      {
        conversationPhaseId = phaseSelected.ConversationPhaseId;
      }

      const requestParams = {
        ConversationId: conversation.ConversationId,
        LayersToHide: layersToHide ?? 0,
        RecycleBinDashboard: !conversation.IsEnable,
        pageNumber: isSubmitTime && pageNumber > 0 ? pageNumber - 1 : pageNumber,
        pageSize,
        isConversationDetailsView: isInfoWindow ? false : isConversationDetailsView,
        ConversationPhaseId: conversationPhaseId,
        replyTab: selectedReplyTab,
      };

      corgeeFunctions?.loadingBarStart?.();

      try
      {
        const response = await ConversationApi.getMoreReplies(api as any, requestParams);
        const normalizedReplies = normalizeImageNames(response, config.weburl);

        corgeeFunctions?.loadingBarComplete?.();

        // The response is the full thread; render only the current window
        const windowedReplies = applyWindow(normalizedReplies);
        setReplies(windowedReplies);
        // Baseline the replies-change effect on what this fetch publishes, so the
        // window growing from a See more is not mistaken for newly added replies
        // (which would inflate the window by a whole extra page).
        prevScopedLenRef.current = scopeToPhase(windowedReplies).length;
        prevRepliesRef.current = windowedReplies;
        onResetRepliesList?.(windowedReplies, !isSubmitTime);
        onRepliesWindowState?.(windowedReplies.length >= normalizedReplies.length);

        if (!isSubmitTime)
        {
          // Carry the new window: the parent merges every attribute, and the
          // closure's conversation still holds the pre-fetch Replies.
          corgeeFunctions.onUpdateConversation(
            conversation.ConversationId,
            { ...conversation, Replies: windowedReplies },
            true,
          );
        }
      }
      catch (error)
      {
        console.error(error);
        corgeeFunctions?.loadingBarComplete?.();
      }
    },
    [
      api,
      applyWindow,
      config.weburl,
      conversation,
      corgeeFunctions,
      isConversationDetailsView,
      isInfoWindow,
      onResetRepliesList,
      onRepliesWindowState,
      pageNumber,
      pageSize,
      phaseSelected,
      scopeToPhase,
      selectedReplyTab,
    ],
  );

  // --------------------------------------------------------------------------
  // Effects
  // --------------------------------------------------------------------------

  // Reset seeMoreReplies on mount
  useEffect(() =>
  {
    resetSeeMoreReplies?.();
  }, [resetSeeMoreReplies]);

  // Handle seeMoreReplies and pageNumber changes
  useEffect(() =>
  {
    if (!conversation || pageNumber < 0) return;

    const seeMoreChanged = prevSeeMoreRepliesRef.current !== seeMoreReplies;
    const pageChanged = prevPageNumberRef.current !== pageNumber;

    // Every "See more" click bumps pageNumber and every collapse flips
    // seeMoreReplies, so any change of either means the window must move.
    if (seeMoreChanged || pageChanged)
    {
      setIsFromAddReply?.(false);
      // One "See more replies" click grows the window by REPLIES_PER_PAGE;
      // "See less" restarts it. The current rows stay rendered while the fetch
      // runs so the new ones merge in place and animate in (keys are stable).
      visibleCountRef.current = seeMoreReplies
        ? Math.max(visibleCountRef.current, COLLAPSED_REPLIES) + REPLIES_PER_PAGE
        : COLLAPSED_REPLIES;
      onLoadingMoreRepliesChange?.(true);
      fetchReplies(false).finally(() => onLoadingMoreRepliesChange?.(false));
    }

    prevSeeMoreRepliesRef.current = seeMoreReplies;
    prevPageNumberRef.current = pageNumber;
  }, [
    conversation,
    seeMoreReplies,
    pageNumber,
    setIsFromAddReply,
    fetchReplies,
    onLoadingMoreRepliesChange,
  ]);

  // Handle submitTime changes
  useEffect(() =>
  {
    if (!conversation) return;
    if (submitTime && !prevSubmitTimeRef.current)
    {
      fetchReplies(true);
    }

    prevSubmitTimeRef.current = submitTime;
  }, [conversation, submitTime, fetchReplies]);

  // Handle conversation replies changes
  useEffect(() =>
  {
    if (!conversation) return;

    // Grow the window when the scoped thread actually gets longer, so an added
    // reply does not push the oldest visible one out and collapse the view back
    // to "See more replies". Keyed on length rather than the tail's ReplyId
    // because an add pushes a temporal reply first and then swaps it for the
    // saved one - two id changes, one length change. Skipped on a tab switch,
    // where the length difference just reflects a different phase.
    const scopedAll = scopeToPhase(conversation.Replies ?? []);
    const phaseChanged = prevActivePhaseIdRef.current !== activePhaseId;

    if (
      !phaseChanged &&
      Number.isFinite(visibleCountRef.current) &&
      prevScopedLenRef.current > 0 &&
      scopedAll.length > prevScopedLenRef.current
    )
    {
      visibleCountRef.current += scopedAll.length - prevScopedLenRef.current;
    }

    prevScopedLenRef.current = scopedAll.length;
    prevActivePhaseIdRef.current = activePhaseId;

    // Windowed mode: whatever arrives (initial full thread, added/edited
    // replies, upstream resets) renders through the window. The new-reply
    // detection below keeps working because the window preserves the tail.
    const nextReplies = applyWindow(conversation.Replies ?? []);
    const currentReplies = prevRepliesRef.current;

    const nextRepliesJson = JSON.stringify(nextReplies);
    const currentRepliesJson = JSON.stringify(currentReplies);

    if (nextRepliesJson && nextRepliesJson !== currentRepliesJson)
    {
      const hasCurrentReplies = currentReplies && currentReplies.length > 0;
      const hasNextReplies = nextReplies && nextReplies.length > 0;

      // Avoid clearing replies if next is empty but current has items
      if (!(hasCurrentReplies && nextReplies?.length === 0))
      {
        const shouldHighlightNew =
          (!hasCurrentReplies && hasNextReplies) ||
          (hasCurrentReplies &&
            hasNextReplies &&
            currentReplies[currentReplies.length - 1]!.ReplyId !==
              nextReplies[nextReplies.length - 1]!.ReplyId);

        if (shouldHighlightNew)
        {
          // The gray bar renders the counters from state. Writing them straight into the DOM
          // with a document-wide selector hit whichever window of the card came first
          // (e.g. the home card while the reply came from the info window).

          // Mark new replies for highlighting
          const updatedReplies = nextReplies.map(nextReply =>
          {
            if (nextReply.reloadReply)
            {
              return { ...nextReply, newReply: false };
            }

            const existsInCurrent = currentReplies.some(
              currentReply => currentReply.ReplyId === nextReply.ReplyId,
            );

            return {
              ...nextReply,
              newReply: !existsInCurrent,
            };
          });

          setReplies(updatedReplies);
          prevRepliesRef.current = updatedReplies;
          return;
        }
      }

      setReplies(nextReplies);
    }

    prevRepliesRef.current = replies;
  }, [conversation, replies, applyWindow, scopeToPhase, activePhaseId]);

  // Handle conversation ID changes
  useEffect(() =>
  {
    if (conversation.ConversationId !== conversationId)
    {
      visibleCountRef.current = COLLAPSED_REPLIES;
      prevScopedLenRef.current = 0;
      setReplies(applyWindow(conversation.Replies || []));
      setConversationId(conversation.ConversationId);
    }
  }, [conversation.ConversationId, conversation.Replies, conversationId, applyWindow]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="corgee_com_card-conversation-item">
      {replies?.map(reply => (
        <div
          key={`reply-${conversation.ConversationId}-${reply.ReplyId}`}
          className="corgee_com_card-conversation-item-reply-container"
          data-react-reply={reply.ReplyId}
        >
          {/* Item Reply */}
          {reply.OriginalConversationId !== undefined &&
            reply.OriginalConversationId > 0 &&
            React.createElement(ItemReply as any, {
              isConvDetailsView,
              conversationId: conversation.ConversationId,
              reply,
              api,
              corgeeFunctions,
              projectId: conversation.ProjectId,
              isConversationDetailsView,
              configMenus,
              config,
            })}

          {/* Single Reply */}
          {(reply.OriginalConversationId === undefined ||
            reply.OriginalConversationId === null ||
            reply.OriginalConversationId === 0) &&
            React.createElement(SingleReply as any, {
              api,
              reply,
              conversationId: conversation.ConversationId,
              conversation,
              onUpdateTotalFavorites,
              corgeeFunctions,
              onDeleteReply,
              isConversationDetailsView,
              onAddRepost,
              setShowRepost,
              onUpdateReply,
              SocketConnectionId,
              configMenus,
              config,
              isInfowindowViewCard,
              repliesPendingToHighlight,
              removePendingHighlight,
              storageData,
              chatData,
              isHomeView,
              ...restProps,
            })}
        </div>
      ))}
    </div>
  );
};

export default ConversationCardReplies;
